/**
 * SchemaGitService — handles creating PRs to the flatbuffer_schemas repo
 * when field engineers add new FlatBuffer message values.
 *
 * Uses the GitHub REST API (via the LiftCloud proxy or direct GitHub token)
 * to:
 *   1. Fetch the current .fbs file contents
 *   2. Append new enum values
 *   3. Create a branch, commit, PR, and merge
 */

import {
  PendingAddition,
  PendingUnionAddition,
  PendingTableFieldAddition,
} from "./SchemaTreeEditor";

// Shared GitHub API constants — used by FlatBufferSchemaTree and SchemaEditorPage
export const GITHUB_API = "https://api.github.com";
export const SCHEMA_REPO_OWNER = "Liftnet2";
export const SCHEMA_REPO_NAME = "flatbuffer_schemas";
export const DEFAULT_SOURCE_BRANCH = "flattened-valued-messages";

/** Fetch available branches from the flatbuffer_schemas repo */
export async function fetchRepoBranches(token: string): Promise<string[]> {
  const resp = await fetch(
    `${GITHUB_API}/repos/${SCHEMA_REPO_OWNER}/${SCHEMA_REPO_NAME}/branches?per_page=100`,
    { headers: githubHeaders(token) },
  );
  if (!resp.ok) throw new Error(`Failed to fetch branches: ${resp.statusText}`);
  const data = await resp.json();
  return data.map((b: { name: string }) => b.name);
}

/** Build GitHub API authorization headers from a PAT */
export function githubHeaders(token: string): Record<string, string> {
  return {
    Authorization: `token ${token}`,
    Accept: "application/vnd.github.v3+json",
  };
}

/** Fetch a single file from the flatbuffer_schemas repo, returning decoded content and SHA */
export async function fetchGitHubFileContent(
  path: string,
  token: string,
  branch: string = DEFAULT_SOURCE_BRANCH,
): Promise<{ content: string; sha: string }> {
  const resp = await fetch(
    `${GITHUB_API}/repos/${SCHEMA_REPO_OWNER}/${SCHEMA_REPO_NAME}/contents/${path}?ref=${branch}`,
    { headers: githubHeaders(token) },
  );
  if (!resp.ok) {
    throw new Error(`Failed to fetch ${path}: ${resp.status} ${resp.statusText}`);
  }
  const data = await resp.json();
  // Decode base64 via TextDecoder for proper UTF-8 handling
  const binary = atob(data.content);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  const decoded = new TextDecoder().decode(bytes);
  return { content: decoded, sha: data.sha };
}

/** Fetch the full repository tree to discover all .fbs files dynamically */
export async function fetchRepoTree(
  token: string,
  branch: string = DEFAULT_SOURCE_BRANCH,
): Promise<{ path: string; sha: string }[]> {
  const resp = await fetch(
    `${GITHUB_API}/repos/${SCHEMA_REPO_OWNER}/${SCHEMA_REPO_NAME}/git/trees/${branch}?recursive=1`,
    { headers: githubHeaders(token) },
  );
  if (!resp.ok) {
    throw new Error(`Failed to fetch repo tree: ${resp.status} ${resp.statusText}`);
  }
  const data = await resp.json();
  return data.tree
    .filter(
      (item: { type: string; path: string }) => item.type === "blob" && item.path.endsWith(".fbs"),
    )
    .map((item: { path: string; sha: string }) => ({
      path: item.path,
      sha: item.sha,
    }));
}

interface FileChange {
  /** Path relative to repo root, e.g. "schemas/ln2/fault.fbs" */
  path: string;
  /** The modified file content */
  content: string;
}

/**
 * Given the raw .fbs file content and an enum name, appends new values
 * to the end of the enum (before the closing brace).
 */
export function appendEnumValues(
  fileContent: string,
  enumName: string,
  newValues: string[],
): string {
  // Match the enum block: `enum EnumName : uint16 { ... }`
  const enumRegex = new RegExp(`(enum\\s+${enumName}\\s*:\\s*\\w+\\s*\\{)([\\s\\S]*?)(\\})`, "m");
  const match = fileContent.match(enumRegex);
  if (!match) {
    throw new Error(`Could not find enum "${enumName}" in file content`);
  }

  const enumHeader = match[1];
  const enumBody = match[2];
  const enumClose = match[3];

  // Trim trailing whitespace from body
  const trimmedBody = enumBody.trimEnd();

  // Check if the last value has a trailing comma; if not, add one
  const lastLine = trimmedBody.split("\n").pop()?.trim() || "";
  const needsComma = lastLine.length > 0 && !lastLine.endsWith(",") && !lastLine.startsWith("//");

  let modifiedBody = trimmedBody;
  if (needsComma) {
    // Find the last non-comment, non-empty value and add a comma
    modifiedBody = `${trimmedBody},`;
  }

  // Append new values
  const additions = newValues.map((v) => `  ${v},`).join("\n");
  const result = `${enumHeader}\n${modifiedBody}\n${additions}\n${enumClose}`;

  return fileContent.replace(enumRegex, result);
}

/**
 * Groups all pending changes by file, then generates the file modifications needed.
 * Handles enum value additions, union member additions, and table field additions.
 */
export function generateFileChanges(
  enumAdditions: PendingAddition[],
  unionAdditions: PendingUnionAddition[],
  tableAdditions: PendingTableFieldAddition[],
  fileContents: Record<string, string>,
): FileChange[] {
  // Collect all file paths that need modification
  const fileMods = new Map<
    string,
    {
      enums: { enumName: string; values: string[] }[];
      unions: { unionName: string; members: string[] }[];
      tables: { tableName: string; fields: { name: string; type: string }[] }[];
    }
  >();

  function getFileMod(filePath: string) {
    if (!fileMods.has(filePath)) {
      fileMods.set(filePath, { enums: [], unions: [], tables: [] });
    }
    return fileMods.get(filePath)!;
  }

  // Group enum additions by file
  enumAdditions.forEach((a) => {
    const filePath = `schemas/${a.enumNode.fbsFile}`;
    const mod = getFileMod(filePath);
    const existing = mod.enums.find((e) => e.enumName === a.enumNode.fbsEnumName);
    if (existing) {
      existing.values.push(a.newValueName);
    } else {
      mod.enums.push({
        enumName: a.enumNode.fbsEnumName,
        values: [a.newValueName],
      });
    }
  });

  // Group union additions by file
  unionAdditions.forEach((a) => {
    const filePath = `schemas/${a.unionNode.fbsFile}`;
    const mod = getFileMod(filePath);
    const existing = mod.unions.find((u) => u.unionName === a.unionNode.fbsUnionName);
    if (existing) {
      existing.members.push(a.newMemberName);
    } else {
      mod.unions.push({
        unionName: a.unionNode.fbsUnionName,
        members: [a.newMemberName],
      });
    }
  });

  // Group table field additions by file
  tableAdditions.forEach((a) => {
    const filePath = `schemas/${a.tableNode.fbsFile}`;
    const mod = getFileMod(filePath);
    const existing = mod.tables.find((t) => t.tableName === a.tableNode.fbsTableName);
    if (existing) {
      existing.fields.push({ name: a.newFieldName, type: a.newFieldType });
    } else {
      mod.tables.push({
        tableName: a.tableNode.fbsTableName,
        fields: [{ name: a.newFieldName, type: a.newFieldType }],
      });
    }
  });

  const changes: FileChange[] = [];

  fileMods.forEach((mod, path) => {
    let content = fileContents[path];
    if (!content) {
      throw new Error(`Missing file content for ${path}`);
    }
    mod.enums.forEach((e) => {
      content = appendEnumValues(content, e.enumName, e.values);
    });
    mod.unions.forEach((u) => {
      content = appendUnionMembers(content, u.unionName, u.members);
    });
    mod.tables.forEach((t) => {
      content = appendTableFields(content, t.tableName, t.fields);
    });
    changes.push({ path, content });
  });

  return changes;
}

/**
 * Generates a descriptive branch name and commit message from all pending changes.
 */
export function generateBranchAndCommitInfo(
  enumAdditions: PendingAddition[],
  unionAdditions: PendingUnionAddition[],
  tableAdditions: PendingTableFieldAddition[],
): {
  branchName: string;
  commitMessage: string;
  prTitle: string;
  prBody: string;
} {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  const totalCount = enumAdditions.length + unionAdditions.length + tableAdditions.length;

  const allNames = [
    ...new Set([
      ...enumAdditions.map((a) => a.enumNode.fbsEnumName),
      ...unionAdditions.map((a) => a.unionNode.fbsUnionName),
      ...tableAdditions.map((a) => a.tableNode.fbsTableName),
    ]),
  ];
  const shortNames = allNames.slice(0, 3).join("-");
  const branchName = `schema-update/${shortNames}-${timestamp}`;

  const changeLines: string[] = [];
  enumAdditions.forEach((a) => {
    changeLines.push(
      `- enum \`${a.enumNode.fbsEnumName}\`: added \`${a.newValueName}\` (index ${a.newValueIndex})`,
    );
  });
  unionAdditions.forEach((a) => {
    changeLines.push(
      `- union \`${a.unionNode.fbsUnionName}\`: added member \`${a.newMemberName}\``,
    );
  });
  tableAdditions.forEach((a) => {
    changeLines.push(
      `- table \`${a.tableNode.fbsTableName}\`: added field \`${a.newFieldName}: ${a.newFieldType}\``,
    );
  });

  const commitMessage = `feat(schema): ${totalCount} schema change(s) to ${allNames.join(", ")}`;
  const prTitle = `feat(schema): ${totalCount} schema change(s)`;

  const allFiles = [
    ...new Set([
      ...enumAdditions.map((a) => `- \`schemas/${a.enumNode.fbsFile}\``),
      ...unionAdditions.map((a) => `- \`schemas/${a.unionNode.fbsFile}\``),
      ...tableAdditions.map((a) => `- \`schemas/${a.tableNode.fbsFile}\``),
    ]),
  ];

  const prBody = `## Summary

${totalCount} schema change(s) via the LiftNet Setup Wizard schema editor.

## Changes

${changeLines.join("\n")}

## Files Modified

${allFiles.join("\n")}

## Notes

After merge, regenerate bindings:
- \`./generate_fbs.bash\` (Rust)
- \`./generate_go.bash\` (Go)
- \`./generate_ts.bash\` (TypeScript)
`;

  return { branchName, commitMessage, prTitle, prBody };
}

/**
 * Appends new members to a FlatBuffer union definition.
 * Works similarly to appendEnumValues but for union types.
 */
export function appendUnionMembers(
  fileContent: string,
  unionName: string,
  newMembers: string[],
): string {
  // Match the union block: `union UnionName { ... }`
  const unionRegex = new RegExp(`(union\\s+${unionName}\\s*\\{)([\\s\\S]*?)(\\})`, "m");
  const match = fileContent.match(unionRegex);
  if (!match) {
    throw new Error(`Could not find union "${unionName}" in file content`);
  }

  const unionHeader = match[1];
  const unionBody = match[2];
  const unionClose = match[3];

  const trimmedBody = unionBody.trimEnd();

  // Check if the last member has a trailing comma
  const lastLine = trimmedBody.split("\n").pop()?.trim() || "";
  const needsComma = lastLine.length > 0 && !lastLine.endsWith(",") && !lastLine.startsWith("//");

  let modifiedBody = trimmedBody;
  if (needsComma) {
    modifiedBody = `${trimmedBody},`;
  }

  const additions = newMembers.map((m) => `  ${m},`).join("\n");
  const result = `${unionHeader}\n${modifiedBody}\n${additions}\n${unionClose}`;

  return fileContent.replace(unionRegex, result);
}

/**
 * Generates a FlatBuffer table definition string (for new tables).
 */
export function createTableDefinition(
  tableName: string,
  fields: { name: string; type: string }[],
): string {
  const fieldLines = fields.map((f) => `  ${f.name}: ${f.type};`).join("\n");
  return `table ${tableName} {\n${fieldLines}\n}\n`;
}

/**
 * Appends new fields to a FlatBuffer table definition.
 * Fields are added before the closing brace.
 */
export function appendTableFields(
  fileContent: string,
  tableName: string,
  newFields: { name: string; type: string }[],
): string {
  // Match the table block: `table TableName { ... }`
  const tableRegex = new RegExp(`(table\\s+${tableName}\\s*\\{)([\\s\\S]*?)(\\})`, "m");
  const match = fileContent.match(tableRegex);
  if (!match) {
    throw new Error(`Could not find table "${tableName}" in file content`);
  }

  const tableHeader = match[1];
  const tableBody = match[2];
  const tableClose = match[3];

  const trimmedBody = tableBody.trimEnd();
  const additions = newFields.map((f) => `  ${f.name}: ${f.type};`).join("\n");
  const result = `${tableHeader}\n${trimmedBody}\n${additions}\n${tableClose}`;

  return fileContent.replace(tableRegex, result);
}

// ************************************************ */
// PR ORCHESTRATION ******************************* */

/**
 * Creates a branch, commits all file changes atomically via the Git Data API,
 * opens a PR, and squash-merges it. Returns the PR URL.
 *
 * Uses the Git Data API (trees + commits) instead of the Contents API so that
 * all file changes are in a single commit regardless of file count.
 */
export async function createAndMergeSchemaPR(
  token: string,
  enumAdditions: PendingAddition[],
  unionAdditions: PendingUnionAddition[],
  tableAdditions: PendingTableFieldAddition[],
  fileContents: Record<string, string>,
  targetBranch: string = DEFAULT_SOURCE_BRANCH,
): Promise<{ prUrl: string; merged: boolean }> {
  const headers = {
    ...githubHeaders(token),
    "Content-Type": "application/json",
  };

  const repoBase = `${GITHUB_API}/repos/${SCHEMA_REPO_OWNER}/${SCHEMA_REPO_NAME}`;

  // Generate modified files
  const changes = generateFileChanges(enumAdditions, unionAdditions, tableAdditions, fileContents);
  const { branchName, commitMessage, prTitle, prBody } = generateBranchAndCommitInfo(
    enumAdditions,
    unionAdditions,
    tableAdditions,
  );

  // 1. Get the SHA of the target branch
  const baseRefResp = await fetch(`${repoBase}/git/ref/heads/${targetBranch}`, {
    headers,
  });
  if (!baseRefResp.ok) throw new Error(`Failed to get base ref: ${baseRefResp.statusText}`);
  const baseRefData = await baseRefResp.json();
  const baseSha = baseRefData.object.sha;

  // 2. Get the base commit to find its tree SHA
  const baseCommitResp = await fetch(`${repoBase}/git/commits/${baseSha}`, {
    headers,
  });
  if (!baseCommitResp.ok)
    throw new Error(`Failed to get base commit: ${baseCommitResp.statusText}`);
  const baseCommitData = await baseCommitResp.json();
  const baseTreeSha = baseCommitData.tree.sha;

  // 3. Create blobs for each changed file
  const blobResults = await Promise.all(
    changes.map(async (change) => {
      const blobResp = await fetch(`${repoBase}/git/blobs`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          content: change.content,
          encoding: "utf-8",
        }),
      });
      if (!blobResp.ok)
        throw new Error(`Failed to create blob for ${change.path}: ${blobResp.statusText}`);
      const blobData = await blobResp.json();
      return { path: change.path, sha: blobData.sha };
    }),
  );

  // 4. Create a new tree with all changes
  const treeResp = await fetch(`${repoBase}/git/trees`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      base_tree: baseTreeSha,
      tree: blobResults.map((b) => ({
        path: b.path,
        mode: "100644",
        type: "blob",
        sha: b.sha,
      })),
    }),
  });
  if (!treeResp.ok) throw new Error(`Failed to create tree: ${treeResp.statusText}`);
  const treeData = await treeResp.json();

  // 5. Create a single atomic commit
  const commitResp = await fetch(`${repoBase}/git/commits`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      message: commitMessage,
      tree: treeData.sha,
      parents: [baseSha],
    }),
  });
  if (!commitResp.ok) throw new Error(`Failed to create commit: ${commitResp.statusText}`);
  const commitData = await commitResp.json();

  // 6. Create branch pointing to the new commit
  const createBranchResp = await fetch(`${repoBase}/git/refs`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      ref: `refs/heads/${branchName}`,
      sha: commitData.sha,
    }),
  });
  if (!createBranchResp.ok)
    throw new Error(`Failed to create branch: ${createBranchResp.statusText}`);

  // 7. Create PR
  const prResp = await fetch(`${repoBase}/pulls`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      title: prTitle,
      body: prBody,
      head: branchName,
      base: targetBranch,
    }),
  });
  if (!prResp.ok) {
    const errData = await prResp.json();
    throw new Error(`Failed to create PR: ${errData.message || prResp.statusText}`);
  }
  const prData = await prResp.json();
  const pullNumber = prData.number;
  const prUrl = prData.html_url;

  // 8. Merge the PR
  const mergeResp = await fetch(`${repoBase}/pulls/${pullNumber}/merge`, {
    method: "PUT",
    headers,
    body: JSON.stringify({
      merge_method: "squash",
      commit_message: commitMessage,
    }),
  });

  return { prUrl, merged: mergeResp.ok };
}
